import React, { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { api } from "@/lib/api";
import { fmtDate } from "@/lib/format";
import LeadDetailSheet from "@/components/LeadDetailSheet";
import { CalendarClock, MessageCircle, ChevronRight } from "lucide-react";
import { ACTIVE_STAGES } from "@/lib/stages";
import CollapsibleWidgetList from "@/components/ui/collapsible-widget-list";

const waLink = (phone, nama) => {
  const d = (phone || "").replace(/\D/g, "");
  const num = d.startsWith("0") ? "62" + d.slice(1) : d;
  return `https://wa.me/${num}?text=${encodeURIComponent("Halo " + (nama || "") + ", ")}`;
};

export default function FollowUpTodayWidget() {
  const [selected, setSelected] = useState(null);
  const { data: leads = [] } = useQuery({ queryKey: ["leads"], queryFn: async () => (await api.get("/leads")).data });
  const today = new Date().toISOString().slice(0, 10);
  const due = leads
    .filter((l) => ACTIVE_STAGES.includes(l.status) && l.follow_up_date && l.follow_up_date.slice(0, 10) <= today)
    .sort((a, b) => (a.follow_up_date || "").localeCompare(b.follow_up_date || ""));

  const dayDiff = (d) => Math.round((new Date(today) - new Date(d.slice(0, 10))) / 86400000);

  return (
    <div className="card-surface rounded-2xl p-5 mb-4" data-testid="followup-today-widget">
      <div className="flex items-center justify-between mb-3">
        <h3 className="font-heading font-semibold flex items-center gap-2"><CalendarClock size={18} className="text-amber-400" />Harus Di-Follow Up Hari Ini</h3>
        <span className="text-xs px-2 py-0.5 rounded-full bg-amber-500/15 text-amber-400" data-testid="followup-count">{due.length}</span>
      </div>
      {due.length === 0 ? (
        <div className="text-center text-zinc-600 text-sm py-6">Tidak ada follow-up jatuh tempo hari ini 🎉</div>
      ) : (
        <CollapsibleWidgetList testId="followup-today" items={due} renderItem={(l) => {
          const diff = dayDiff(l.follow_up_date);
          const label = diff <= 0 ? "Hari ini" : `Telat ${diff} hari`;
          return (
            <div key={l.lead_id} data-testid={`followup-row-${l.lead_id}`} className="flex items-center gap-3 p-3 rounded-xl bg-[#18181B] hover:bg-white/[0.03] transition-colors">
              <div className="min-w-0 flex-1 cursor-pointer" onClick={() => setSelected(l.lead_id)}>
                <div className="font-medium truncate flex items-center gap-2">{l.nama}
                  <span className={`text-[10px] px-1.5 py-0.5 rounded shrink-0 ${diff > 0 ? "bg-red-500/15 text-red-400" : "bg-amber-500/15 text-amber-400"}`}>{label}</span>
                </div>
                <div className="text-[11px] text-zinc-500">{l.no_hp || "-"} · {l.status} · {l.sales || ""}</div>
              </div>
              <a href={waLink(l.no_hp, l.nama)} target="_blank" rel="noreferrer" onClick={(e) => e.stopPropagation()} data-testid={`followup-wa-${l.lead_id}`}
                className="shrink-0 w-8 h-8 rounded-lg bg-emerald-600/20 text-emerald-400 flex items-center justify-center hover:bg-emerald-600/30"><MessageCircle size={15} /></a>
              <ChevronRight size={16} className="text-zinc-600 shrink-0 cursor-pointer" onClick={() => setSelected(l.lead_id)} />
            </div>
          );
        }} />
      )}
      <LeadDetailSheet leadId={selected} onClose={() => setSelected(null)} />
    </div>
  );
}
